import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { fireLeadConversion, injectAllPixels } from "@/lib/pixelTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_PATHS = ["/privacy", "/terms", "/legal-partners"];
const CFG_KEY = { meta: "meta_pixel_id", google_ads: "google_ads_id", ga4: "ga4_measurement_id", tiktok: "tiktok_pixel_id" };
const fired = new Set();

function surfaceFor(pathname, q) {
  if (pathname === "/lp/thank-you") return { surface: "thank_you", slug: q.get("funnel") || q.get("slug") };
  const m = pathname.match(/^\/lp\/([^/?#]+)/);
  if (m) return { surface: "landing", slug: m[1] };
  return SITE_PATHS.includes(pathname) ? { surface: "site", slug: null } : null;
}

function cfgFor(px) {
  const cfg = { [CFG_KEY[px.kind]]: px.pixel_id };
  if (px.kind === "google_ads" && px.conversion_label) cfg.google_ads_conversion_label = px.conversion_label;
  return cfg;
}

function injectCode(px) {
  if (document.getElementById(`site-px-${px.id}`)) return;
  const box = document.createElement("div");
  box.id = `site-px-${px.id}`;
  box.style.display = "none";
  box.innerHTML = px.code;
  box.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script");
    Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
    s.text = old.textContent;
    old.replaceWith(s);
  });
  document.body.appendChild(box);
}

// Loads after the page is ready so extra pixels never slow the first paint.
function whenIdle(fn) {
  const run = () => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1));
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
}

/** Extra company pixels (as many as admins add) on public pages; skipped on previews, tests and samples. */
export function SitePixels() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(search);
    const where = surfaceFor(pathname, q);
    if (!where || q.get("preview") === "1" || q.get("demo") || q.get("test") === "1" || q.get("pixel_check")) return;
    if ((where.slug || "").startsWith("sample-")) return;
    const leadId = q.get("lead_id") || "";
    const realLead = where.surface === "thank_you" && leadId && !leadId.toUpperCase().startsWith("DEMO-");
    let cancelled = false;
    whenIdle(() => {
      axios.get(`${API}/public/site-pixels`, { params: { surface: where.surface, slug: where.slug || undefined } })
        .then(({ data }) => {
          if (cancelled) return;
          (data.pixels || []).forEach((px) => {
            if (px.kind === "custom") { injectCode(px); return; }
            const cfg = cfgFor(px);
            injectAllPixels(cfg);
            const key = `${px.id}:${leadId}`;
            if (realLead && !fired.has(key)) {
              fired.add(key);
              fireLeadConversion(cfg, { reference: leadId.slice(0, 8).toUpperCase() });
            }
          });
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [pathname, search]);
  return null;
}
